import {
  Component,
  ElementRef,
  ViewChild,
  AfterViewInit,
  HostListener,
  Inject,
  PLATFORM_ID,
  Input, OnDestroy, DestroyRef, inject,
} from '@angular/core';
import {
  CommonModule,
  NgClass,
  NgFor,
  isPlatformBrowser,
} from '@angular/common';
import { RouterLink, RouterModule, Router } from '@angular/router';
import { DashboardService } from '../../../_services/dashboard/dashboard.service';
import { InrShortPipe } from '../../../_pipes';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AuthService } from '../../../_services/auth.service';
import { mapCiReport } from '../../search/view-area/ci-report';
import { rentalYieldIndicator } from '../../search/view-area/ci-indicators';
import { backendBadgeLabel } from '../../../_helpers/backend-badge-label';

@Component({
  selector: 'app-slider',
  standalone: true,
  imports: [CommonModule, NgClass, NgFor, RouterModule, InrShortPipe, RouterLink],
  templateUrl: './slider.component.html',
  styleUrl: './slider.component.scss',
})
export class SliderComponent implements AfterViewInit, OnDestroy {
  readonly backendBadgeLabel = backendBadgeLabel;
  @Input() showHeading = true;
  @Input() autoScrollSpeed = 1.1;
  private readonly destroyRef = inject(DestroyRef);
  private destroyed = false;
  private motionQuery?: MediaQueryList;
  private readonly onMotionChange = () => this.motionQuery?.matches ? this.stopAutoScroll() : this.startAutoScroll();
  private readonly pendingTimers = new Set<ReturnType<typeof setTimeout>>();
  @ViewChild('slider') slider!: ElementRef<HTMLElement>;
  items = [1, 2, 3, 4, 5];


  activeIndex = 0;
  private ctaShown = false;

  private isDragging = false;
  private startX = 0;
  private scrollStart = 0;

  private autoScrollId: number | undefined;
  carouselData: any = [];
  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    private _DS: DashboardService,
    private router: Router,
    private authService: AuthService
  ) {}

  ngAfterViewInit() {
    if (!isPlatformBrowser(this.platformId)) return;

    this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.motionQuery.addEventListener('change', this.onMotionChange);

    this.getCarouselData();
  }
  loading = true;
  loadError = false;
  dynamicloopImages: any = [];
  ciVerdict(property: any): string {
    const verdict = [property?.circledIntelligenceVerdict, property?.adjustedVerdict, property?.initialVerdict, mapCiReport(property)?.verdict]
      .find(value => typeof value === 'string' && !!value.trim());
    return verdict?.replace(/[🔵🟠🟡🟢🔴🟣🟤⚫⚪●]/gu, '').trim() ?? '';
  }

  ciRentalYield(property: any): string {
    // Raw calculator yields are ratios, consistent with the existing CI mapper.
    const value = property?.rentalYield;
    if ((typeof value === 'number' || typeof value === 'string' && /^[-+]?\d+(?:\.\d+)?$/.test(value.trim())) && Number.isFinite(Number(value))) {
      return `${(Number(value) * 100).toFixed(1)}%`;
    }
    if (typeof value === 'string' && /^\d+(?:\.\d+)?%$/.test(value.trim())) return value.trim();
    return rentalYieldIndicator(mapCiReport(property));
  }

  getCarouselData() {
    this.loading = true;
    this.loadError = false;
    this.stopAutoScroll();
    this._DS.getPropertyCarousel().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({ next: (res: any) => {
      this.loading = false;
      const properties = res?.data?.properties;
      this.loadError = res?.status === false || !Array.isArray(properties);
      this.carouselData = !this.loadError ? properties : [];
      this.activeIndex = 0;

      this.dynamicloopImages = this.carouselData;
      this.schedule(() => {
        this.slider.nativeElement.scrollLeft = 0;
        this.startAutoScroll();
      }, 100);
    }, error: () => {
      this.loading = false;
      this.loadError = true;
      this.carouselData = [];
      this.dynamicloopImages = [];
    } });
  }
  getPriceRange(prices: any[]): { min: string; max: string; isSame: boolean } {
    if (!prices || prices.length === 0)
      return { min: '0', max: '0', isSame: true };

    const values = prices.map((p) => p.price).sort((a, b) => a - b);
    const min = values[0];
    const max = values[values.length - 1];

    return {
      min: min.toString(),
      max: max.toString(),
      isSame: min === max,
    };
  }
  formatAddress(item: any): string {
    if (!item) return '';

    return [
      item.name,
      item.address,
      item.streetName,
      item.cityName,
      item.stateName,
    ]
      .filter((part) => part && part.trim() !== '') // Removes null, undefined, or empty strings
      .join(' '); // Joins them with a single space
  }

  startAutoScroll() {
    this.stopAutoScroll();
    if (!this.carouselData.length || this.destroyed || this.isDragging || !isPlatformBrowser(this.platformId) || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const el = this.slider.nativeElement;

    let fractionalScroll = 0;
    const loop = () => {
      if (this.destroyed || this.isDragging) return;
      // Accumulate subpixel movement so slower speeds work in browsers that round scrollLeft.
      fractionalScroll += this.autoScrollSpeed;
      const pixels = Math.floor(fractionalScroll);
      fractionalScroll -= pixels;
      el.scrollLeft += pixels;
      const maxScroll = el.scrollWidth - el.clientWidth;

      this.autoScrollId = requestAnimationFrame(loop);
    };

    this.autoScrollId = requestAnimationFrame(loop);
  }

  stopAutoScroll() {
    if (this.autoScrollId !== undefined) cancelAnimationFrame(this.autoScrollId);
    this.autoScrollId = undefined;
  }

  /* ---------- ACTIVE INDEX ---------- */

  onScroll() {
    if (!this.carouselData.length) return;
    const el = this.slider.nativeElement;
    const center = el.scrollLeft + el.clientWidth / 2;
    const cards = el.querySelectorAll<HTMLElement>('.card-container');

    cards.forEach((card, i) => {
      const cardCenter = card.offsetLeft + card.offsetWidth / 2;

      if (Math.abs(center - cardCenter) < card.offsetWidth * 0.6) {
        this.activeIndex = i % this.carouselData.length;
      }
    });

    // Show CTA only once per visit to the last card
    const reachedEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 10;

    if (reachedEnd && !this.ctaShown && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {

      this.ctaShown = true;

      this.stopAutoScroll();

      // Wait on the Explore card
      this.schedule(() => {

        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
          this.ctaShown = false;
          return;
        }
        el.scrollTo({
          left: 0,
          behavior: 'smooth'
        });

        // Restart after scrolling back
        this.schedule(() => {
          this.ctaShown = false;
          this.startAutoScroll();
        }, 1200);

      }, 5000);
    }
  }

  /* ---------- DRAG ---------- */

  onPointerDown(e: PointerEvent) {
    this.isDragging = true;
    this.stopAutoScroll();

    this.startX = e.pageX;
    this.scrollStart = this.slider.nativeElement.scrollLeft;

    this.slider.nativeElement.setPointerCapture(e.pointerId);
  }

  onPointerMove(e: PointerEvent) {
    if (!this.isDragging) return;

    const dx = e.pageX - this.startX;
    this.slider.nativeElement.scrollLeft = this.scrollStart - dx;
  }

  @HostListener('window:pointercancel')
  @HostListener('window:pointerup')
  onPointerUp() {
    if (!this.isDragging) return;
    this.isDragging = false;
    this.startAutoScroll();
  }

  moveSlide(direction: number): void {
    const count = this.carouselData.length;
    if (!count) return;
    this.scrollToIndex((this.activeIndex + direction + count) % count);
  }

  scrollToIndex(index: number) {
    this.stopAutoScroll();
    const el = this.slider.nativeElement;
    const cards = el.querySelectorAll<HTMLElement>('.card-container');
    const card = cards[index];
    if (!card) return;

    const left = card.offsetLeft - el.clientWidth / 2 + card.clientWidth / 2;

    el.scrollTo({ left, behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
    this.schedule(() => this.startAutoScroll(), 4000);
  }

  private schedule(callback: () => void, delay: number): void {
    const timer = setTimeout(() => {
      this.pendingTimers.delete(timer);
      if (!this.destroyed) callback();
    }, delay);
    this.pendingTimers.add(timer);
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.motionQuery?.removeEventListener('change', this.onMotionChange);
    this.stopAutoScroll();
    this.pendingTimers.forEach(timer => clearTimeout(timer));
    this.pendingTimers.clear();
  }

  isActive(i: number): boolean {
    return !!this.carouselData.length && this.activeIndex === i % this.carouselData.length;
  }

  formatPropertyDetails(priceArray: any[]): string {
    if (!priceArray || priceArray.length === 0) {
      return 'No details available';
    }

    const firstPrice = priceArray[0];
    const formattedArea = new Intl.NumberFormat('en-US').format(
      firstPrice.areaInSq
    );

    return `${firstPrice.bhkType} Apartments<br>${formattedArea} Sq. Ft`;
  }
  isView(property: any) {
    // The search auth guard preserves this property URL through sign-in.
    void this.router.navigate(['/search'], {
      queryParams: {
        name: property.name,
        id: property._id,
      },
    });
  }
  exploreAll() {
    // The search guard handles login and retains the intended destination.
    void this.router.navigate(['/search']);
  }
}
